import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import QRCode from 'react-qr-code';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import ServiceToggle from './ServiceToggle';
import { Label } from './ui/label';
import { LayoutActionButton } from './Buttons';
import ActionButton from './ui/action-button';
import { Shield, Eye, FileText, CheckCircle, Download, FileDown, Lock, History, ChevronRight, Check, ShieldCheck, Fingerprint, QrCode, Copy, Mail, Phone as PhoneIcon, Send } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import useOperatorLocale from '../hooks/useOperatorLocale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import jsPDF from 'jspdf';

// SWR fetcher using apiV2
const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

// Rate limiting for PDF exports (1 export per 5 seconds)
const EXPORT_COOLDOWN_MS = 5000;

const WaiversTabClean = () => {
  const { locale } = useOperatorLocale();
  const isUSA = (locale?.country || 'US').toUpperCase() === 'US';
  const [saving, setSaving] = useState(false);
  
  // Waivers are ALWAYS enabled - this is a business requirement
  const [waiversEnabled, setWaiversEnabled] = useState(true);
  const [waiverText, setWaiverText] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // signedWaivers is now derived via useMemo below from bookings + standalone
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Version tracking
  const [currentVersionOverride, setCurrentVersionOverride] = useState(null);
  const setCurrentVersion = setCurrentVersionOverride;
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [selectedHistoryVersion, setSelectedHistoryVersion] = useState(null);
  const [originalWaiverText, setOriginalWaiverText] = useState('');
  
  // Export rate limiting
  const lastExportRef = useRef(0);
  const [exportCooldown, setExportCooldown] = useState(false);

  // Walk-Up Waiver — send link state
  const [sendEmail, setSendEmail] = useState('');
  const [sendPhone, setSendPhone] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  const handleSendWaiverLink = async (delivery) => {
    let recipient = delivery === 'email' ? sendEmail.trim() : sendPhone.trim();
    if (!recipient) { toast.error(`Enter a ${delivery === 'email' ? 'email address' : 'phone number'}`); return; }

    // Normalize phone: accept 3037175022, +13037175022, 13037175022 → always +1XXXXXXXXXX
    if (delivery === 'sms') {
      const digits = recipient.replace(/\D/g, '');
      if (digits.length === 10) recipient = `+1${digits}`;
      else if (digits.length === 11 && digits.startsWith('1')) recipient = `+${digits}`;
      else if (!recipient.startsWith('+')) recipient = `+1${digits}`;
      if (!/^\+1\d{10}$/.test(recipient)) {
        toast.error('Please enter a valid 10-digit US phone number');
        return;
      }
    }

    const setter = delivery === 'email' ? setSendingEmail : setSendingSms;
    setter(true);
    try {
      await apiV2.post(`/organizations/${orgId}/settings/standalone-waivers/send-link`, { delivery, recipient });
      toast.success(`Waiver link sent via ${delivery === 'email' ? 'email' : 'SMS'}`);
      delivery === 'email' ? setSendEmail('') : setSendPhone('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setter(false);
    }
  };
  
  // Integrity hash data from org
  const [currentWaiverHash, setCurrentWaiverHash] = useState(null);
  const [currentWaiverHashedAt, setCurrentWaiverHashedAt] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const waiversPerPage = 10;
  
  // Sorting
  const [sortBy, setSortBy] = useState('recent'); // 'recent' or 'oldest'
  
  // Fetch organization settings with SWR using v2 API
  const orgId = typeof window !== 'undefined' ? localStorage.getItem('organizationId') : null;
  const { data: orgData, isLoading: loadingOrg, mutate: mutateOrg } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true }
  );

  // Derived: prefer the locally-saved version (after save) else what came from server
  const currentVersion = currentVersionOverride || (orgData?.waiverVersion) || '1.0';
  
  // Fetch bookings for signed waivers
  const { data: bookingsData, isLoading: loadingBookings } = useSWR(
    orgId ? `/organizations/${orgId}/bookings` : null,
    fetcher,
    { revalidateOnFocus: true }
  );

  // Fetch standalone walk-up waiver signatures
  const { data: standaloneData } = useSWR(
    orgId ? `settings/${orgId}/standalone-waivers` : null,
    () => apiV2.get(`/organizations/${orgId}/settings/standalone-waivers?limit=200`).then(r => r.data),
    { revalidateOnFocus: true }
  );

  const loading = loadingOrg || loadingBookings;

  // Debounced waiver search — calls backend, not client-side filter
  const [waiverSearch, setWaiverSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [waiverPage, setWaiverPage] = useState(1);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [dateField, setDateField] = useState('booking'); // 'booking' | 'signed'
  const [exportingWaivers, setExportingWaivers] = useState(false);
  const WAIVER_LIMIT = 10;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(waiverSearch); setWaiverPage(1); }, 350);
    return () => clearTimeout(t);
  }, [waiverSearch]);

  // Reset page when filters change (mark intent — set inline in handlers below
  // to avoid set-state-in-effect)

  const waiverQueryString = (() => {
    const p = new URLSearchParams({
      q: debouncedSearch,
      page: String(waiverPage),
      limit: String(WAIVER_LIMIT),
      sort_by: sortBy,
      date_field: dateField,
    });
    if (dateStart) p.append('start_date', dateStart);
    if (dateEnd) p.append('end_date', dateEnd);
    return p.toString();
  })();

  const { data: waiversSearchData, isValidating: waiversLoading } = useSWR(
    orgId ? `waivers-search-${orgId}-${waiverQueryString}` : null,
    () => apiV2.get(
      `/organizations/${orgId}/settings/waivers/search?${waiverQueryString}`
    ).then(r => r.data),
    { revalidateOnFocus: true, keepPreviousData: true }
  );

  const searchedWaivers = waiversSearchData?.waivers || [];
  const waiversTotal = waiversSearchData?.total || 0;
  const waiversPages = waiversSearchData?.pages || 1;

  const handleExportWaivers = async () => {
    try {
      setExportingWaivers(true);
      const p = new URLSearchParams({ q: debouncedSearch, date_field: dateField });
      if (dateStart) p.append('start_date', dateStart);
      if (dateEnd) p.append('end_date', dateEnd);
      const res = await apiV2.get(
        `/organizations/${orgId}/settings/waivers/export?${p.toString()}`,
        { responseType: 'blob' }
      );
      const rowCount = res.headers['x-row-count'];
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `waivers_${dateStart || 'all'}_to_${dateEnd || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${rowCount || ''} waiver records`);
    } catch (err) {
      console.error('Waiver export failed:', err);
      toast.error('Could not export waivers');
    } finally {
      setExportingWaivers(false);
    }
  };

  // Update local state when org data is fetched
  useEffect(() => {
    if (orgData) {
      setWaiversEnabled(true);
      setWaiverText(orgData.customWaiverText || '');
      setOriginalWaiverText(orgData.customWaiverText || '');
      setVersionHistory(orgData.waiverVersionHistory || []);
      setCurrentWaiverHash(orgData.currentWaiverIntegrityHash || null);
      setCurrentWaiverHashedAt(orgData.currentWaiverHashedAt || null);
    }
  }, [orgData]);

  // Merge booking waivers + standalone walk-up waivers into one list (derived)
  const signedWaivers = React.useMemo(() => {
    const waivers = [];

    // Booking-linked waivers
    if (bookingsData?.bookings) {
      bookingsData.bookings.forEach(trip => {
        const signatures = trip.waiverSignatures || (trip.waiverData?.signatures) || [];
        if (signatures.length > 0) {
          signatures.forEach(sig => {
            waivers.push({
              ...sig,
              source: 'booking',
              trip: { id: trip.id, date: trip.bookingDate, startTime: trip.startTime, confirmationCode: trip.confirmationCode }
            });
          });
        } else if (trip.waiverSigned && !signatures.length) {
          waivers.push({
            signatureName: trip.customerName,
            signedAt: trip.waiverSignedAt,
            verificationMethod: 'unknown',
            source: 'booking',
            trip: { id: trip.id, date: trip.bookingDate, startTime: trip.startTime, confirmationCode: trip.confirmationCode }
          });
        }
      });
    }

    // Standalone walk-up waivers (no booking)
    if (standaloneData?.signatures) {
      standaloneData.signatures.forEach(sig => {
        // Parse deviceInfo — could be JSON string or plain user-agent string
        let parsedDevice = {};
        if (sig.deviceInfo) {
          try { parsedDevice = typeof sig.deviceInfo === 'string' ? JSON.parse(sig.deviceInfo) : sig.deviceInfo; }
          catch { parsedDevice = { userAgent: sig.deviceInfo }; }
        }
        waivers.push({
          signatureName: sig.signerName,
          signedAt: sig.signedAt,
          waiverVersion: sig.waiverVersion,
          verificationMethod: sig.verificationMethod || 'Unknown',
          verifiedContact: sig.verifiedContact,
          phone: sig.phone,
          email: sig.email,
          signatureData: sig.signatureData,
          deviceInfo: parsedDevice,
          ipAddress: sig.ipAddress,
          source: 'standalone',
          trip: null,
        });
      });
    }

    // Sort newest first
    waivers.sort((a, b) => new Date(b.signedAt || 0) - new Date(a.signedAt || 0));
    return waivers;
  }, [bookingsData, standaloneData]);
  
  // Check if waiver text has changed (needs new version)
  const hasWaiverTextChanged = waiverText !== originalWaiverText && originalWaiverText !== '';
  
  // Calculate next version number
  const getNextVersion = () => {
    const [major, minor] = currentVersion.split('.').map(Number);
    if (minor >= 9) {
      return `${major + 1}.0`;
    }
    return `${major}.${minor + 1}`;
  };

  // Helper function to log waiver audit events
  const logWaiverAudit = async (eventType, details = {}) => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      await apiV2.post('/activity-log/waiver-audit', {
        eventType,
        performedBy: userData.username || 'Unknown',
        waiverVersion: currentVersion,
        integrityHash: currentWaiverHash,
        ...details
      });
    } catch (error) {
      console.error('Error logging waiver audit event:', error);
      // Don't block the main action if logging fails
    }
  };

  const handleSave = async () => {
    if (waiversEnabled && waiverText.length < 100) {
      toast.error('Waiver text must be at least 100 characters');
      return;
    }

    setSaving(true);
    try {
      // Prepare update data
      const updateData = {
        waiversEnabled: waiversEnabled,
        customWaiverText: waiverText
      };
      
      // If waiver text changed, create new version
      if (hasWaiverTextChanged) {
        const newVersion = getNextVersion();
        const now = new Date().toISOString();
        
        // Add current version to history before updating
        const newHistoryEntry = {
          version: currentVersion,
          text: originalWaiverText,
          createdAt: versionHistory.length > 0 
            ? versionHistory[versionHistory.length - 1]?.updatedAt || now
            : now,
          updatedAt: now
        };
        
        updateData.waiverVersion = newVersion;
        updateData.waiverVersionHistory = [...versionHistory, newHistoryEntry];
        
        await apiV2.patch('/auth/organizations/me', updateData);
        
        // Log waiver audit: Version created
        await logWaiverAudit('waiver_version_created', {
          previousVersion: currentVersion,
          waiverVersion: newVersion,
          previousHash: currentWaiverHash,
          metadata: {
            textLengthBefore: originalWaiverText.length,
            textLengthAfter: waiverText.length,
            timestamp: now
          }
        });
        
        // Update local state
        setCurrentVersion(newVersion);
        setVersionHistory([...versionHistory, newHistoryEntry]);
        setOriginalWaiverText(waiverText);
        
        toast.success(`Waiver updated to version ${newVersion}!`);
      } else {
        await apiV2.patch('/auth/organizations/me', updateData);
        
        // Log waiver audit: Settings changed (no version change)
        await logWaiverAudit('waiver_settings_changed', {
          metadata: { changeType: 'settings_only' }
        });
        
        toast.success('Waiver settings saved successfully!');
      }
      
      // Refresh org data
      mutateOrg();
      
      // Notify setup guide of data change
      window.dispatchEvent(new Event('setupDataChanged'));
    } catch (error) {
      console.error('Error saving waiver settings:', error);
      toast.error('Failed to save waiver settings');
    } finally {
      setSaving(false);
    }
  };

  const viewWaiverDetails = async (waiver) => {
    setSelectedWaiver(waiver);
    setShowDetailModal(true);
    
    // Log activity: Viewing sensitive waiver data
    try {
      await apiV2.post('/activity-log/log-waiver-access', {
        waiverSignatureName: waiver.signatureName,
        tripId: waiver.trip?.id,
        accessedBy: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'Unknown',
        accessReason: 'Admin viewing waiver details'
      });
    } catch (error) {
      console.error('Error logging waiver access:', error);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <TabPageLayout
      title="Waivers"
      subtitle="Configure digital waivers for customer bookings"
      loading={false}
      actions={
        <LayoutActionButton
          onClick={handleSave}
          loading={saving}
          disabled={saving || waiverText.length < 100}
          icon={CheckCircle}
          data-testid="waivers-save-btn"
        >
          Save
        </LayoutActionButton>
      }
    >
      <div className="space-y-6">
        {/* Waiver text + version history */}
        <div className="card-soft p-5 sm:p-6">
          <div className="mb-5">
            <h3 className="settings-section-title">Waiver Text</h3>
            <p className="settings-section-subtitle">
              Required for every booking. Edits create a new version — old signatures stay tied to the version they signed.
            </p>
          </div>

          <Textarea
            value={waiverText}
            onChange={(e) => setWaiverText(e.target.value)}
            placeholder="Paste or type your liability waiver text here…"
            className="min-h-[260px] text-sm sm:text-base"
            data-testid="waiver-text-input"
          />

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-blue-600 font-medium">v{currentVersion}</span>
              {hasWaiverTextChanged && (
                <span className="text-amber-600 text-xs font-medium">→ v{getNextVersion()} on save</span>
              )}
              {versionHistory.length > 0 && (
                <button
                  onClick={() => {
                    setShowVersionHistoryModal(true);
                    logWaiverAudit('waiver_history_accessed', {
                      metadata: { totalVersions: versionHistory.length }
                    });
                  }}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm"
                  data-testid="waiver-history-btn"
                >
                  View history ({versionHistory.length})
                </button>
              )}
              {waiverText && (
                <button
                  onClick={() => setShowPreviewModal(true)}
                  className="text-gray-600 hover:text-gray-900 hover:underline text-sm"
                  data-testid="waiver-preview-btn"
                >
                  Preview
                </button>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-500">
              {waiverText.length} characters
              {waiverText.length > 0 && waiverText.length < 100 && (
                <span className="text-red-600 ml-2">({100 - waiverText.length} more needed)</span>
              )}
              {waiverText.length >= 100 && (
                <span className="text-green-600 ml-2">Minimum met</span>
              )}
            </p>
          </div>
        </div>

        {/* Walk-Up Waiver Link */}
        {waiverText && (() => {
          const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
          const orgSlug = orgData?.slug || '';
          const signUrl = `${frontendUrl}/sign/${orgSlug}`;
          return (
            <div className="card-soft p-5 sm:p-6">
              <div className="mb-5">
                <h3 className="settings-section-title">Walk-up Link</h3>
                <p className="settings-section-subtitle">
                  Share with walk-ins or groups who don&apos;t have a booking yet.
                </p>
              </div>

              <div className="space-y-5">
                {/* URL + copy */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Signing link</label>
                  <div className="flex gap-2">
                    <input readOnly value={signUrl}
                      className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-gray-700"
                      data-testid="walkup-sign-url" />
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(signUrl); toast.success('Link copied'); }}
                      className="gap-1.5 flex-shrink-0">
                      <Copy className="w-4 h-4" /> Copy
                    </Button>
                  </div>
                </div>

                {/* QR + actions */}
                <div className="flex items-start gap-5 flex-wrap">
                  <div id="walkup-qr-container" className="bg-white p-4 border border-gray-200 rounded-xl inline-block">
                    <QRCode value={signUrl} size={140} level="M" />
                  </div>
                  <div className="flex flex-col gap-2 pt-1">
                    <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto justify-start"
                      onClick={() => {
                        const svg = document.querySelector('#walkup-qr-container svg');
                        if (!svg) return;
                        const xml = new XMLSerializer().serializeToString(svg);
                        const blob = new Blob([xml], { type: 'image/svg+xml' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url;
                        a.download = `waiver-qr-${orgSlug}.svg`; a.click();
                        URL.revokeObjectURL(url);
                        toast.success('QR downloaded');
                      }}
                      data-testid="walkup-download-qr">
                      <Download className="w-4 h-4" /> Download QR
                    </Button>
                    <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto justify-start"
                      onClick={() => setShowPreviewModal(true)}
                      data-testid="walkup-preview-waiver">
                      <Eye className="w-4 h-4" /> Preview waiver
                    </Button>
                  </div>
                </div>

                {/* Send by email */}
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-gray-400" /> Send by email
                  </label>
                  <div className="flex gap-2">
                    <Input type="email" placeholder="customer@email.com" value={sendEmail}
                      onChange={e => setSendEmail(e.target.value)} className="h-10 text-sm"
                      data-testid="walkup-email-input" />
                    <Button size="sm" onClick={() => handleSendWaiverLink('email')} disabled={sendingEmail || !sendEmail.includes('@')}
                      className="gap-1.5 flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white">
                      <Send className="w-4 h-4" /> {sendingEmail ? 'Sending…' : 'Send'}
                    </Button>
                  </div>
                </div>

                {/* Send by SMS — US operators only */}
                {isUSA && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
                      <PhoneIcon className="w-3.5 h-3.5 text-gray-400" /> Send by SMS
                    </label>
                    <div className="flex gap-2">
                      <Input type="tel" placeholder="US phone number" value={sendPhone}
                        onChange={e => setSendPhone(e.target.value.replace(/[^0-9+()\-\s]/g, '').slice(0, 16))}
                        maxLength={16} className="h-10 text-sm"
                        data-testid="walkup-sms-input" />
                      <Button size="sm" onClick={() => handleSendWaiverLink('sms')} disabled={sendingSms || sendPhone.replace(/\D/g,'').length < 7}
                        className="gap-1.5 flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white">
                        <Send className="w-4 h-4" /> {sendingSms ? 'Sending…' : 'Send'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Signed Waivers */}
        <div className="card-soft p-5 sm:p-6">
          <div className="mb-5 flex items-baseline justify-between gap-3">
            <div>
              <h3 className="settings-section-title">
                Signed Waivers{waiversTotal > 0 ? ` (${waiversTotal})` : ''}
              </h3>
              <p className="settings-section-subtitle">
                Every signature with device fingerprint and verification trail.
              </p>
            </div>
          </div>

          {signedWaivers.length === 0 && !debouncedSearch && !dateStart && !dateEnd ? (
            <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg">
              <Shield className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-600">No signed waivers yet</p>
              <p className="text-xs text-gray-400 mt-1">They&apos;ll appear here as customers sign</p>
            </div>
          ) : (
            <>
              {/* Sort Filter */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * waiversPerPage) + 1}-{Math.min(currentPage * waiversPerPage, signedWaivers.length)} of {signedWaivers.length}
                </p>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when sorting changes
                  }}
                  className="text-sm border border-gray-300 rounded px-3 py-1"
                >
                  <option value="recent">Most Recent First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>

              {/* Search + Date filter + Export */}
              <div className="mb-4 space-y-2">
                <Input
                  placeholder="Search by name, phone, email or booking code..."
                  value={waiverSearch}
                  onChange={e => { setWaiverSearch(e.target.value); setWaiverPage(1); }}
                  className="h-10 text-sm border-gray-200"
                  data-testid="waivers-search-input"
                />
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-gray-500">From</label>
                    <Input
                      type="date"
                      value={dateStart}
                      onChange={(e) => { setDateStart(e.target.value); setWaiverPage(1); }}
                      className="h-9 text-sm"
                      data-testid="waivers-date-from"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="text-xs text-gray-500">To</label>
                    <Input
                      type="date"
                      value={dateEnd}
                      onChange={(e) => { setDateEnd(e.target.value); setWaiverPage(1); }}
                      className="h-9 text-sm"
                      data-testid="waivers-date-to"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">By</label>
                    <select
                      value={dateField}
                      onChange={(e) => { setDateField(e.target.value); setWaiverPage(1); }}
                      className="h-9 text-sm border border-gray-200 rounded-md px-2 bg-white"
                      data-testid="waivers-date-field"
                    >
                      <option value="booking">Booking date</option>
                      <option value="signed">Signature date</option>
                    </select>
                  </div>
                  {(dateStart || dateEnd || waiverSearch) && (
                    <button
                      type="button"
                      onClick={() => { setDateStart(''); setDateEnd(''); setWaiverSearch(''); }}
                      className="h-9 px-3 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
                      data-testid="waivers-clear-filters"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleExportWaivers}
                    disabled={exportingWaivers}
                    className="h-9 px-3 text-xs font-medium text-white rounded-md shadow-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    data-testid="waivers-export-btn"
                  >
                    {exportingWaivers ? 'Exporting…' : 'Export CSV'}
                  </button>
                </div>
              </div>

              {/* Waivers List — results from backend search */}
              <div className="space-y-3">
                {waiversLoading && searchedWaivers.length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">Searching...</div>
                )}
                {!waiversLoading && searchedWaivers.length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    {debouncedSearch ? `No waivers matching "${debouncedSearch}"` : 'No waivers signed yet'}
                  </div>
                )}
                {searchedWaivers.map((waiver, idx) => (
                    <div
                      key={idx}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                      onClick={() => viewWaiverDetails(waiver)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900">{waiver.signatureName}</p>
                            {(waiver.waiverVersion || waiver.version) && (
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                                v{waiver.waiverVersion || waiver.version}
                              </span>
                            )}
                          </div>
                          {waiver.source === 'standalone' ? (
                            <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                              {waiver.phone && <p>📱 {waiver.phone}</p>}
                              {waiver.email && <p>✉️ {waiver.email}</p>}
                              {waiver.verificationMethod && <p className="text-xs text-gray-400">Verified via {waiver.verificationMethod}</p>}
                              {!waiver.phone && !waiver.email && <p className="text-gray-400">No contact info provided</p>}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 mt-1">
                              {(() => {
                                try {
                                  // DATE RULE: YYYY-MM-DD → local midnight (never UTC)
                                  const d = waiver.trip?.date ? new Date(waiver.trip.date + 'T00:00:00') : null;
                                  const dateStr = d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                                  const timeStr = waiver.trip?.startTime ? (() => {
                                    const [h, m] = waiver.trip.startTime.split(':').map(Number);
                                    const ampm = h >= 12 ? 'PM' : 'AM';
                                    const h12 = h % 12 || 12;
                                    return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
                                  })() : '';
                                  return dateStr ? `Booking: ${dateStr}${timeStr ? ' at ' + timeStr : ''}` : 'Booking details';
                                } catch { return 'Booking: ' + (waiver.trip?.date || ''); }
                              })()}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Signed: {waiver.signedAt ? (() => {
                              try {
                                const d = new Date(waiver.signedAt);
                                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' +
                                  d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                              } catch { return waiver.signedAt; }
                            })() : 'Unknown'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="badge-app badge-app-success">
                            Verified
                          </span>
                          <p className="text-xs text-gray-500">Click for details</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              
              {/* Pagination */}
              {waiversPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <Button variant="outline" size="sm"
                    onClick={() => setWaiverPage(p => Math.max(1, p - 1))}
                    disabled={waiverPage === 1}>Previous</Button>
                  <span className="text-sm text-gray-600">
                    Page {waiverPage} of {waiversPages} &middot; {waiversTotal} total
                  </span>
                  <Button variant="outline" size="sm"
                    onClick={() => setWaiverPage(p => Math.min(waiversPages, p + 1))}
                    disabled={waiverPage >= waiversPages}>Next</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Waiver Detail Modal - ALL SENSITIVE DATA */}
      {selectedWaiver && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Waiver Signature Details
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Version & Agreement Badge */}
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                  v{selectedWaiver.waiverVersion || '1.0'}
                </span>
                <span className="px-3 py-1.5 bg-green-100 text-green-800 text-sm font-semibold rounded-full flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" />
                  Agreed to Waiver
                </span>
              </div>
              
              {/* Signer Info - Clean layout */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Signer Name</p>
                  <p className="text-base font-semibold text-gray-900">{selectedWaiver.signatureName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Signed At</p>
                  <p className="text-base font-semibold text-gray-900">{new Date(selectedWaiver.signedAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Booking</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedWaiver.trip?.confirmationCode || 'N/A'}
                    {selectedWaiver.trip?.date && <span className="text-gray-500 font-normal"> • {selectedWaiver.trip.date}</span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Verification</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedWaiver.verificationMethod === 'email' ? 'Email OTP' :
                     (selectedWaiver.verificationMethod === 'sms' || selectedWaiver.verificationMethod === 'phone') ? 'SMS OTP' :
                     selectedWaiver.verificationMethod === 'id' ? 'ID Document' :
                     selectedWaiver.verificationMethod === 'team_member_id_check' ? 'Staff ID Check' :
                     'Unknown'}
                  </p>
                </div>
                {(selectedWaiver.verificationMethod === 'email' || selectedWaiver.verificationMethod === 'sms' || selectedWaiver.verificationMethod === 'phone') && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      {selectedWaiver.verificationMethod === 'email' ? 'Verified Email' : 'Verified Phone'}
                    </p>
                    <p className="text-base font-mono text-gray-900">
                      {selectedWaiver.verificationMethod === 'email' 
                        ? (selectedWaiver.email || selectedWaiver.verifiedContact || 'N/A')
                        : (selectedWaiver.phoneNumber || selectedWaiver.verifiedContact || 'N/A')}
                    </p>
                  </div>
                )}
              </div>

              {/* Signature Image */}
              {(() => {
                // Get signature - prefer signatureData (new), fallback to signatureUrl/signatureImage
                const getSignatureSource = () => {
                  // New format: signatureData is compressed JPEG base64
                  if (selectedWaiver.signatureData && selectedWaiver.signatureData.startsWith('data:')) {
                    return selectedWaiver.signatureData;
                  }
                  // Legacy: signatureUrl (file path)
                  const sigUrl = selectedWaiver.signatureUrl || selectedWaiver.signatureDataUrl || selectedWaiver.signatureImage;
                  if (!sigUrl) return null;
                  // If it's a data URL or already has protocol, use as-is
                  if (sigUrl.startsWith('data:') || sigUrl.startsWith('http')) return sigUrl;
                  // Prepend API base URL for relative paths
                  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                  return `${apiBase}${sigUrl}`;
                };
                const signatureSrc = getSignatureSource();
                
                return signatureSrc ? (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Digital Signature</p>
                    <div className="border border-gray-200 rounded-lg p-4 bg-white">
                      <img 
                        src={signatureSrc} 
                        alt="Signature" 
                        className="max-h-24 w-auto"
                        onError={(e) => {
                          console.error('Failed to load signature image');
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Digital Signature</p>
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 text-center">
                      <p className="text-sm text-gray-500">Signature image not available</p>
                      <p className="text-xs text-gray-400 mt-1">Signed via checkbox agreement</p>
                    </div>
                  </div>
                );
              })()}

              {/* Device Fingerprint - Terminal style */}
              <div className="bg-gray-900 rounded-lg p-4">
                <p className="text-xs text-green-400 font-mono uppercase tracking-wide mb-3">Device Fingerprint</p>
                <div className="space-y-1.5 font-mono text-xs">
                  <div className="flex">
                    <span className="text-gray-500 w-28">IP</span>
                    <span className="text-white">{selectedWaiver.ipAddress || 'Not captured'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Platform</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.platform || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Screen</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.screenResolution || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Timezone</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.timezone || 'Unknown'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-500 w-28">Language</span>
                    <span className="text-white">{selectedWaiver.deviceInfo?.language || 'Unknown'}</span>
                  </div>
                  {selectedWaiver.deviceInfo?.userAgent && (
                    <div className="pt-2 border-t border-gray-700 mt-2">
                      <span className="text-gray-500">User Agent</span>
                      <p className="text-green-400 text-[10px] break-all mt-1 leading-relaxed">
                        {selectedWaiver.deviceInfo.userAgent}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Legal Notice - Concise */}
              <p className="text-xs text-gray-500 border-t border-gray-200 pt-4">
                This signature is legally binding. Device data and IP address stored for compliance. Access logged.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Waiver Preview Modal */}
      {showPreviewModal && (
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Waiver Preview</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <style>
                {`
                  @media print {
                    body { margin: 0.5in; }
                    #waiver-print-content { 
                      max-width: 100%; 
                      page-break-inside: avoid;
                    }
                    #waiver-print-content h3 { 
                      page-break-after: avoid; 
                    }
                    #waiver-print-content .signature-section {
                      page-break-before: auto;
                      margin-top: 1in;
                    }
                  }
                `}
              </style>
              <div className="bg-white border border-gray-200 rounded-lg p-8" id="waiver-print-content">
                <h3 className="font-bold text-xl mb-6 text-center">Waiver Agreement</h3>
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-8" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                  {waiverText}
                </div>
                
                {/* Signature Fields for Physical Print */}
                <div className="border-t-2 border-gray-300 pt-8 mt-8 signature-section">
                  <p className="text-xs text-gray-600 mb-6">
                    By signing below, I acknowledge that I have read, understood, and agree to the terms of this waiver.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">PRINT NAME</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">SIGNATURE</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">DATE</p>
                      <div className="border-b-2 border-gray-400 h-10"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end print:hidden">
                <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                  Close
                </Button>
                <Button onClick={() => window.print()} style={{ backgroundColor: '#2563EB' }} className="hover:opacity-90 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Print Waiver
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Version History Modal */}
      {showVersionHistoryModal && (
        <Dialog open={showVersionHistoryModal} onOpenChange={setShowVersionHistoryModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Waiver Version History
              </DialogTitle>
            </DialogHeader>
            
            {/* Export buttons row */}
            <div className="flex justify-between items-center mb-2 gap-2">
              <p className="text-xs text-gray-500">
                {exportCooldown ? 'Please wait before exporting again...' : 'Export official legal documents'}
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={exportCooldown}
                onClick={() => {
                  // Rate limiting check
                  const now = Date.now();
                  if (now - lastExportRef.current < EXPORT_COOLDOWN_MS) {
                    toast.error('Please wait a few seconds before exporting again');
                    return;
                  }
                  lastExportRef.current = now;
                  setExportCooldown(true);
                  setTimeout(() => setExportCooldown(false), EXPORT_COOLDOWN_MS);
                  
                  // Helper: Format date with ordinal suffix
                  const formatDateWithOrdinal = (dateStr) => {
                    const date = new Date(dateStr);
                    const day = date.getDate();
                    const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                    const month = date.toLocaleString('en-US', { month: 'long' });
                    const year = date.getFullYear();
                    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                    return `${month} ${day}${suffix}, ${year} at ${time} ${tz}`;
                  };
                  
                  // Helper: Add waiver text with proper pagination
                  const addFullWaiverText = (doc, text, startY, pageWidth, pageHeight) => {
                    let yPos = startY;
                    const lineHeight = 4;
                    const margin = 20;
                    const maxY = pageHeight - 30; // Leave room for footer
                    
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'normal');
                    const lines = doc.splitTextToSize(text || 'No waiver text configured.', pageWidth - (margin * 2));
                    
                    for (let i = 0; i < lines.length; i++) {
                      if (yPos > maxY) {
                        doc.addPage();
                        yPos = 20;
                      }
                      doc.text(lines[i], margin, yPos);
                      yPos += lineHeight;
                    }
                    
                    return yPos + 5;
                  };
                  
                  // Create PDF document
                  const doc = new jsPDF();
                  const orgName = localStorage.getItem('organizationName') || 'Organization';
                  const pageWidth = doc.internal.pageSize.getWidth();
                  const pageHeight = doc.internal.pageSize.getHeight();
                  let yPos = 20;
                  
                  // ===== HEADER =====
                  doc.setFontSize(18);
                  doc.setFont('helvetica', 'bold');
                  doc.text('WAIVER VERSION HISTORY', pageWidth / 2, yPos, { align: 'center' });
                  yPos += 8;
                  
                  doc.setFontSize(12);
                  doc.setFont('helvetica', 'normal');
                  doc.text('Official Legal Document - Complete Record', pageWidth / 2, yPos, { align: 'center' });
                  yPos += 15;
                  
                  // ===== DOCUMENT INFO =====
                  doc.setFontSize(10);
                  doc.setFont('helvetica', 'bold');
                  doc.text('Organization:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(orgName, 55, yPos);
                  yPos += 6;
                  
                  doc.setFont('helvetica', 'bold');
                  doc.text('Generated:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(formatDateWithOrdinal(new Date().toISOString()), 55, yPos);
                  yPos += 6;
                  
                  doc.setFont('helvetica', 'bold');
                  doc.text('Current Version:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(`v${currentVersion}`, 55, yPos);
                  yPos += 6;
                  
                  doc.setFont('helvetica', 'bold');
                  doc.text('Total Versions:', 20, yPos);
                  doc.setFont('helvetica', 'normal');
                  doc.text(`${versionHistory.length + 1}`, 55, yPos);
                  yPos += 10;
                  
                  // Integrity hash if available
                  if (currentWaiverHash) {
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Integrity Hash:', 20, yPos);
                    doc.setFont('courier', 'normal');
                    doc.text(currentWaiverHash.substring(0, 32) + '...', 55, yPos);
                    yPos += 5;
                    doc.setFont('helvetica', 'italic');
                    doc.setTextColor(100, 100, 100);
                    doc.text('HMAC-SHA256 - Tamper-proof verification', 55, yPos);
                    doc.setTextColor(0, 0, 0);
                    yPos += 8;
                  }
                  
                  // ===== CURRENT WAIVER (FULL TEXT) =====
                  doc.setDrawColor(200, 200, 200);
                  doc.line(20, yPos, pageWidth - 20, yPos);
                  yPos += 10;
                  
                  doc.setFontSize(12);
                  doc.setFont('helvetica', 'bold');
                  doc.text(`CURRENT WAIVER (v${currentVersion}) - COMPLETE TEXT`, 20, yPos);
                  yPos += 8;
                  
                  // Add FULL waiver text (no truncation)
                  yPos = addFullWaiverText(doc, waiverText, yPos, pageWidth, pageHeight);
                  
                  doc.setFontSize(8);
                  doc.setFont('helvetica', 'italic');
                  doc.setTextColor(100, 100, 100);
                  doc.text(`${waiverText?.length || 0} characters`, 20, yPos);
                  doc.setTextColor(0, 0, 0);
                  yPos += 10;
                  
                  // ===== VERSION HISTORY (FULL TEXT FOR EACH) =====
                  if (versionHistory.length > 0) {
                    [...versionHistory].reverse().forEach((version) => {
                      // New page for each version for clarity
                      doc.addPage();
                      yPos = 20;
                      
                      // Version header
                      doc.setDrawColor(200, 200, 200);
                      doc.setFillColor(245, 245, 245);
                      doc.rect(20, yPos - 5, pageWidth - 40, 20, 'F');
                      
                      doc.setFontSize(12);
                      doc.setFont('helvetica', 'bold');
                      doc.text(`VERSION ${version.version}`, 25, yPos + 5);
                      
                      doc.setFontSize(9);
                      doc.setFont('helvetica', 'normal');
                      doc.text(formatDateWithOrdinal(version.updatedAt || version.createdAt), 25, yPos + 12);
                      yPos += 25;
                      
                      // Integrity hash if available
                      if (version.integrityHash) {
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'bold');
                        doc.text('Integrity Hash:', 20, yPos);
                        doc.setFont('courier', 'normal');
                        doc.text(version.integrityHash.substring(0, 32) + '...', 55, yPos);
                        yPos += 5;
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 100);
                        doc.text(`${version.hashAlgorithm || 'HMAC-SHA256'} - Verified Authentic`, 55, yPos);
                        doc.setTextColor(0, 0, 0);
                        yPos += 8;
                      }
                      
                      // Full waiver text for this version
                      yPos = addFullWaiverText(doc, version.text, yPos, pageWidth, pageHeight);
                      
                      doc.setFontSize(8);
                      doc.setFont('helvetica', 'italic');
                      doc.setTextColor(100, 100, 100);
                      doc.text(`${version.text?.length || 0} characters`, 20, yPos);
                      doc.setTextColor(0, 0, 0);
                    });
                  }
                  
                  // ===== FOOTER ON ALL PAGES =====
                  const pageCount = doc.internal.getNumberOfPages();
                  for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(128, 128, 128);
                    doc.text(
                      `Official Waiver Record - ${orgName} - Page ${i} of ${pageCount}`,
                      pageWidth / 2,
                      pageHeight - 10,
                      { align: 'center' }
                    );
                    doc.setTextColor(0, 0, 0);
                  }
                  
                  // Save PDF
                  doc.save(`waiver-complete-history-${orgName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
                  
                  // Log audit: Full history exported
                  logWaiverAudit('waiver_history_exported', {
                    exportFormat: 'PDF',
                    versionsIncluded: versionHistory.length + 1, // history + current
                    metadata: {
                      includesCurrentVersion: true,
                      totalVersions: versionHistory.length + 1
                    }
                  });
                  
                  toast.success('Complete waiver history exported as PDF');
                }}
                className="flex items-center gap-1.5 text-xs"
              >
                <FileDown className="w-4 h-4" />
                Export All Versions (Full Text)
              </Button>
            </div>
            
            <div className="space-y-4">
              {/* Current Version */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-900">v{currentVersion}</span>
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                      Current
                    </span>
                  </div>
                  {/* Export single version button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={exportCooldown}
                    onClick={() => {
                      // Rate limiting
                      const now = Date.now();
                      if (now - lastExportRef.current < EXPORT_COOLDOWN_MS) {
                        toast.error('Please wait before exporting again');
                        return;
                      }
                      lastExportRef.current = now;
                      setExportCooldown(true);
                      setTimeout(() => setExportCooldown(false), EXPORT_COOLDOWN_MS);
                      
                      // Export single version
                      const doc = new jsPDF();
                      const orgName = localStorage.getItem('organizationName') || 'Organization';
                      const pageWidth = doc.internal.pageSize.getWidth();
                      const pageHeight = doc.internal.pageSize.getHeight();
                      let yPos = 20;
                      
                      const formatDateWithOrdinal = (dateStr) => {
                        const date = new Date(dateStr);
                        const day = date.getDate();
                        const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                        const month = date.toLocaleString('en-US', { month: 'long' });
                        const year = date.getFullYear();
                        const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                        return `${month} ${day}${suffix}, ${year} at ${time} ${tz}`;
                      };
                      
                      // Header
                      doc.setFontSize(18);
                      doc.setFont('helvetica', 'bold');
                      doc.text('OFFICIAL WAIVER DOCUMENT', pageWidth / 2, yPos, { align: 'center' });
                      yPos += 8;
                      
                      doc.setFontSize(12);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`Version ${currentVersion} - Complete Legal Text`, pageWidth / 2, yPos, { align: 'center' });
                      yPos += 15;
                      
                      // Document info
                      doc.setFontSize(10);
                      doc.setFont('helvetica', 'bold');
                      doc.text('Organization:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(orgName, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Version:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`v${currentVersion}`, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Generated:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(formatDateWithOrdinal(new Date().toISOString()), 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Status:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.setTextColor(0, 128, 0);
                      doc.text('ACTIVE - Current Version', 55, yPos);
                      doc.setTextColor(0, 0, 0);
                      yPos += 10;
                      
                      // Integrity hash
                      if (currentWaiverHash) {
                        doc.setFillColor(240, 253, 244);
                        doc.rect(20, yPos - 3, pageWidth - 40, 18, 'F');
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'bold');
                        doc.text('TAMPER-PROOF VERIFICATION', 25, yPos + 2);
                        doc.setFont('helvetica', 'normal');
                        doc.text(`Integrity Hash (HMAC-SHA256): ${currentWaiverHash.substring(0, 48)}...`, 25, yPos + 8);
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 100);
                        doc.text('This hash proves the document has not been modified since creation.', 25, yPos + 13);
                        doc.setTextColor(0, 0, 0);
                        yPos += 22;
                      }
                      
                      // Divider
                      doc.setDrawColor(200, 200, 200);
                      doc.line(20, yPos, pageWidth - 20, yPos);
                      yPos += 10;
                      
                      // Full waiver text
                      doc.setFontSize(9);
                      doc.setFont('helvetica', 'normal');
                      const lines = doc.splitTextToSize(waiverText || 'No waiver text.', pageWidth - 40);
                      const lineHeight = 4;
                      const maxY = pageHeight - 25;
                      
                      for (let i = 0; i < lines.length; i++) {
                        if (yPos > maxY) {
                          doc.addPage();
                          yPos = 20;
                        }
                        doc.text(lines[i], 20, yPos);
                        yPos += lineHeight;
                      }
                      
                      // Character count
                      yPos += 5;
                      doc.setFontSize(8);
                      doc.setFont('helvetica', 'italic');
                      doc.setTextColor(128, 128, 128);
                      doc.text(`Document length: ${waiverText?.length || 0} characters`, 20, yPos);
                      doc.setTextColor(0, 0, 0);
                      
                      // Footer on all pages
                      const pageCount = doc.internal.getNumberOfPages();
                      for (let i = 1; i <= pageCount; i++) {
                        doc.setPage(i);
                        doc.setFontSize(8);
                        doc.setTextColor(128, 128, 128);
                        doc.text(
                          `Official Legal Document - ${orgName} - v${currentVersion} - Page ${i} of ${pageCount}`,
                          pageWidth / 2,
                          pageHeight - 10,
                          { align: 'center' }
                        );
                        doc.setTextColor(0, 0, 0);
                      }
                      
                      doc.save(`waiver-v${currentVersion}-${orgName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
                      
                      // Log audit: Current version exported
                      logWaiverAudit('waiver_exported_pdf', {
                        exportFormat: 'PDF',
                        waiverVersion: currentVersion,
                        integrityHash: currentWaiverHash,
                        metadata: { exportType: 'current_version' }
                      });
                      
                      toast.success(`Waiver v${currentVersion} exported as PDF`);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    Export
                  </Button>
                </div>
                <p className="text-xs text-blue-700">
                  Active waiver version • Last updated {(() => {
                    const date = new Date(currentWaiverHashedAt || new Date());
                    const day = date.getDate();
                    const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                    const month = date.toLocaleString('en-US', { month: 'short' });
                    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
                    const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                    return `${month} ${day}${suffix} ${time} ${tz}`;
                  })()}
                </p>
                {/* Integrity hash display */}
                {currentWaiverHash && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-green-700 font-medium">Tamper-proof</span>
                    <code className="text-gray-500 font-mono text-[10px] bg-white px-1.5 py-0.5 rounded">
                      {currentWaiverHash.substring(0, 16)}...
                    </code>
                  </div>
                )}
              </div>
              
              {/* Previous Versions */}
              {versionHistory.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <History className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">No previous versions</p>
                  <p className="text-gray-400 text-xs mt-1">Version history will appear here when you make changes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Previous Versions (Read-Only)</h4>
                  {[...versionHistory].reverse().map((version, index) => {
                    // Format date with ordinal suffix and timezone
                    const dateStr = version.updatedAt || version.createdAt;
                    let formattedDate = 'Date unknown';
                    
                    if (dateStr) {
                      try {
                        const date = new Date(dateStr);
                        if (!isNaN(date.getTime())) {
                          const day = date.getDate();
                          const suffix = ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10 ? day % 10 : 0)];
                          const month = date.toLocaleString('en-US', { month: 'short' });
                          const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
                          const tz = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                          formattedDate = `${month} ${day}${suffix} ${time} ${tz}`;
                        }
                      } catch (e) {
                        console.warn('Date parsing error:', e);
                      }
                    }
                    
                    // Export single historical version
                    const exportSingleVersion = () => {
                      // Rate limiting
                      const now = Date.now();
                      if (now - lastExportRef.current < EXPORT_COOLDOWN_MS) {
                        toast.error('Please wait before exporting again');
                        return;
                      }
                      lastExportRef.current = now;
                      setExportCooldown(true);
                      setTimeout(() => setExportCooldown(false), EXPORT_COOLDOWN_MS);
                      
                      const doc = new jsPDF();
                      const orgName = localStorage.getItem('organizationName') || 'Organization';
                      const pageWidth = doc.internal.pageSize.getWidth();
                      const pageHeight = doc.internal.pageSize.getHeight();
                      let yPos = 20;
                      
                      const formatDateLong = (dateStr) => {
                        const d = new Date(dateStr);
                        const dy = d.getDate();
                        const suf = ['th', 'st', 'nd', 'rd'][dy % 10 > 3 ? 0 : (dy % 100 - dy % 10 !== 10 ? dy % 10 : 0)];
                        const mo = d.toLocaleString('en-US', { month: 'long' });
                        const yr = d.getFullYear();
                        const tm = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        const tzn = d.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                        return `${mo} ${dy}${suf}, ${yr} at ${tm} ${tzn}`;
                      };
                      
                      // Header
                      doc.setFontSize(18);
                      doc.setFont('helvetica', 'bold');
                      doc.text('OFFICIAL WAIVER DOCUMENT', pageWidth / 2, yPos, { align: 'center' });
                      yPos += 8;
                      
                      doc.setFontSize(12);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`Version ${version.version} - Historical Record`, pageWidth / 2, yPos, { align: 'center' });
                      yPos += 15;
                      
                      // Document info
                      doc.setFontSize(10);
                      doc.setFont('helvetica', 'bold');
                      doc.text('Organization:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(orgName, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Version:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(`v${version.version}`, 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Created:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.text(formatDateLong(version.updatedAt || version.createdAt), 55, yPos);
                      yPos += 6;
                      
                      doc.setFont('helvetica', 'bold');
                      doc.text('Status:', 20, yPos);
                      doc.setFont('helvetica', 'normal');
                      doc.setTextColor(128, 128, 128);
                      doc.text('ARCHIVED - Historical Version', 55, yPos);
                      doc.setTextColor(0, 0, 0);
                      yPos += 10;
                      
                      // Integrity hash
                      if (version.integrityHash) {
                        doc.setFillColor(240, 253, 244);
                        doc.rect(20, yPos - 3, pageWidth - 40, 18, 'F');
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'bold');
                        doc.text('TAMPER-PROOF VERIFICATION', 25, yPos + 2);
                        doc.setFont('helvetica', 'normal');
                        doc.text(`Integrity Hash (${version.hashAlgorithm || 'HMAC-SHA256'}): ${version.integrityHash.substring(0, 48)}...`, 25, yPos + 8);
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 100);
                        doc.text('This hash proves the document has not been modified since creation.', 25, yPos + 13);
                        doc.setTextColor(0, 0, 0);
                        yPos += 22;
                      }
                      
                      // Divider
                      doc.setDrawColor(200, 200, 200);
                      doc.line(20, yPos, pageWidth - 20, yPos);
                      yPos += 10;
                      
                      // Full waiver text
                      doc.setFontSize(9);
                      doc.setFont('helvetica', 'normal');
                      const lines = doc.splitTextToSize(version.text || 'No waiver text.', pageWidth - 40);
                      const lineHeight = 4;
                      const maxY = pageHeight - 25;
                      
                      for (let i = 0; i < lines.length; i++) {
                        if (yPos > maxY) {
                          doc.addPage();
                          yPos = 20;
                        }
                        doc.text(lines[i], 20, yPos);
                        yPos += lineHeight;
                      }
                      
                      // Character count
                      yPos += 5;
                      doc.setFontSize(8);
                      doc.setFont('helvetica', 'italic');
                      doc.setTextColor(128, 128, 128);
                      doc.text(`Document length: ${version.text?.length || 0} characters`, 20, yPos);
                      doc.setTextColor(0, 0, 0);
                      
                      // Footer on all pages
                      const pageCount = doc.internal.getNumberOfPages();
                      for (let i = 1; i <= pageCount; i++) {
                        doc.setPage(i);
                        doc.setFontSize(8);
                        doc.setTextColor(128, 128, 128);
                        doc.text(
                          `Historical Record - ${orgName} - v${version.version} - Page ${i} of ${pageCount}`,
                          pageWidth / 2,
                          pageHeight - 10,
                          { align: 'center' }
                        );
                        doc.setTextColor(0, 0, 0);
                      }
                      
                      doc.save(`waiver-v${version.version}-${orgName.replace(/\s+/g, '-').toLowerCase()}-archived.pdf`);
                      
                      // Log audit: Historical version exported
                      logWaiverAudit('waiver_exported_pdf', {
                        exportFormat: 'PDF',
                        waiverVersion: version.version,
                        integrityHash: version.integrityHash,
                        metadata: { exportType: 'historical_version' }
                      });
                      
                      toast.success(`Waiver v${version.version} exported as PDF`);
                    };
                    
                    return (
                      <div 
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800">v{version.version}</span>
                            {version.integrityHash && (
                              <span className="flex items-center gap-1 text-[10px] text-green-600">
                                <Fingerprint className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{formattedDate}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={exportCooldown}
                              onClick={exportSingleVersion}
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 h-6 px-2"
                            >
                              <FileDown className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setSelectedHistoryVersion(selectedHistoryVersion === version.version ? null : version.version)}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            {selectedHistoryVersion === version.version ? 'Hide' : 'View'} waiver text
                          </button>
                        </div>
                        
                        {selectedHistoryVersion === version.version && (
                          <div className="mt-3 p-4 bg-white border border-gray-300 rounded-md">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-gray-400" />
                                <span className="text-xs text-gray-500 font-medium">READ-ONLY</span>
                              </div>
                              {version.integrityHash && (
                                <code className="text-[10px] text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                  Hash: {version.integrityHash.substring(0, 12)}...
                                </code>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                              {version.text}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              {version.text?.length || 0} characters
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Legal Notice with integrity explanation */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Previous versions are preserved for legal compliance and cannot be modified.
                </p>
                <div className="mt-2 flex items-start gap-2 text-xs text-gray-400">
                  <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-medium text-gray-600">Tamper-proof integrity:</span> Each version is cryptographically signed with HMAC-SHA256. 
                    If any content is modified in the database, the integrity verification will fail, providing legal proof of authenticity.
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </TabPageLayout>
  );
};

export default WaiversTabClean;
